import React from 'react';

/**
 * Top-level error boundary.
 *
 * Two responsibilities:
 * 1. Detect ChunkLoadError / "Loading chunk" failures that happen when a user
 *    has an old cached index.html pointing at chunk filenames that no longer
 *    exist on the server after a redeploy (very common on Safari due to its
 *    aggressive HTML caching). We auto-reload ONCE and set a sessionStorage
 *    flag to prevent infinite reload loops.
 * 2. For any other unhandled render/lifecycle error, show a friendly
 *    "Something went wrong" card with a reload button so the user isn't
 *    stuck on a blank white screen.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    const message = String(error?.message || error || '');
    const isChunkLoadError =
      error?.name === 'ChunkLoadError' ||
      /Loading chunk [\d]+ failed/i.test(message) ||
      /Loading CSS chunk/i.test(message) ||
      /Failed to fetch dynamically imported module/i.test(message);

    if (isChunkLoadError) {
      // Guard against infinite reload loops if the deploy itself is broken
      const RELOAD_KEY = 'chunk_reload_attempted_at';
      try {
        const last = Number(sessionStorage.getItem(RELOAD_KEY) || 0);
        if (Date.now() - last > 30_000) {
          sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
          window.location.reload();
          return;
        }
      } catch {
        // sessionStorage disabled (Safari Private Browsing) — reload once anyway
        window.location.reload();
        return;
      }
    }

    // Log for observability
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', error, errorInfo);
  }

  handleReload = () => {
    try { sessionStorage.removeItem('chunk_reload_attempted_at'); } catch {}
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-sky-50 to-amber-50 p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center" data-testid="error-boundary-card">
          <div className="w-14 h-14 rounded-full bg-rose-100 mx-auto mb-4 flex items-center justify-center">
            <svg className="w-7 h-7 text-rose-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          </div>
          <h1 className="text-xl font-heading font-bold text-slate-800 mb-2">Something went wrong</h1>
          <p className="text-slate-500 text-sm mb-6">
            The page ran into an unexpected error. This often clears up with a quick refresh.
            If it keeps happening, please contact your team administrator.
          </p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={this.handleReload}
              className="px-5 py-2.5 rounded-full bg-primary text-white font-medium hover:bg-primary/90"
              data-testid="error-boundary-reload-btn"
            >
              Reload page
            </button>
            <button
              onClick={this.handleGoHome}
              className="px-5 py-2.5 rounded-full border border-slate-200 text-slate-700 font-medium hover:bg-slate-50"
              data-testid="error-boundary-home-btn"
            >
              Go to home
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
